<template>
  <v-sheet>
    <v-card-text>
      <h5 class="font-weight-bold mb-1 text-h5">
        Account Verification
      </h5>

      <div
        v-if="isLoadingScreenVisible"
        class="align-center d-flex flex-column pt-4"
      >
        <v-progress-circular
          color="primary"
          indeterminate
          size="60"
        />
        <p
          class="pt-4"
        >
          Please wait
        </p>
      </div>

      <div
        v-if="isRequestFormScreenVisible"
      >
        <p>In order to use LawLab you need to verify your account first.</p>
        <p>To do that an e-mail will be sent with a verification link that you must open.</p>
        <p>If you have not received it, please fill in your details below.</p>
        <v-form
          class="pt-4"
          @submit.prevent
        >
          <v-text-field
            v-model="login"
            label="Username"
            outlined
            prepend-inner-icon="mdi-account"
          />

          <v-text-field
            v-model="email"
            label="E-mail address"
            outlined
            prepend-inner-icon="mdi-email"
          />
        </v-form>
        <div
          class="d-flex"
        >
          <v-btn
            text
            @click="closeScreen"
          >
            <v-icon
              left
            >
              mdi-arrow-left
            </v-icon>
            Back
          </v-btn>

          <v-spacer />

          <v-btn
            text
            @click="requestAccountVerification"
          >
            Request
            <v-icon
              right
            >
              mdi-email-send
            </v-icon>
          </v-btn>
        </div>
      </div>

      <div
        v-if="isRequestFailedScreenVisible"
      >
        <p>There was an error while making the request.</p>

        <v-alert
          v-for="(error, index) of errors"
          :key="index"
          type="error"
        >
          {{ error }}
        </v-alert>

        <p>Please try again and, if the error persists, contact your administrator.</p>

        <div
          class="d-flex"
        >
          <v-btn
            text
            @click="showRequestFormScreen"
          >
            <v-icon
              left
            >
              mdi-arrow-left
            </v-icon>
            Back
          </v-btn>

          <v-spacer />
        </div>
      </div>

      <div
        v-if="isRequestSuccessfulScreenVisible"
      >
        <p>An e-mail with a verification link has been sent to your e-mail address.</p>
        <p>You should open it in order to verify your account.</p>
        <p>Thank you.</p>
      </div>

      <div
        v-if="isVerificationFailedScreenVisible"
      >
        <p>The verification link is invalid or has expired.</p>
        <p>If you haven't validated your account please request a new verification link.</p>
        <div
          class="d-flex"
        >
          <v-btn
            text
            @click="showRequestFormScreen"
          >
            <v-icon
              left
            >
              mdi-arrow-left
            </v-icon>
            Back
          </v-btn>

          <v-spacer />
        </div>
      </div>

      <div
        v-if="isVerificationSuccessfulScreenVisible"
      >
        <div
          class="d-flex justify-center"
        >
          <SuccessIcon
            class="pb-6 primary--text pt-2"
            style="width: 128px;"
          />
        </div>

        <p>Your account has been verified.</p>

        <p>Please head to the login page and enter your credentials in order to start using LawLab.</p>

        <p>Thank you.</p>

        <div
          class="d-flex justify-center"
        >
          <v-btn
            text
            @click="closeScreen"
          >
            <v-icon
              left
            >
              mdi-account
            </v-icon>
            Login Screen
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-sheet>
</template>

<script>
import SuccessIcon from '@/components/Icons/SuccessIcon'
import { USER_NAMESPACE } from '@/store/user'
import { mapActions } from 'vuex'

const Screens = {
  Loading: 0,
  RequestFailed: 2,
  RequestForm: 1,
  RequestSuccessful: 3,
  VerificationFailed: 4,
  VerificationSuccessful: 5,
}

export default {
  name: 'AccountVerification',

  components: {
    SuccessIcon,
  },

  data () {
    return {
      currentScreen: Screens.Loading,
      email: '',
      errors: [],
      login: '',
    }
  },

  computed: {
    isLoadingScreenVisible () {
      return this.currentScreen === Screens.Loading
    },

    isRequestFailedScreenVisible () {
      return this.currentScreen === Screens.RequestFailed
    },

    isRequestFormScreenVisible () {
      return this.currentScreen === Screens.RequestForm
    },

    isRequestSuccessfulScreenVisible () {
      return this.currentScreen === Screens.RequestSuccessful
    },

    isVerificationFailedScreenVisible () {
      return this.currentScreen === Screens.VerificationFailed
    },

    isVerificationSuccessfulScreenVisible () {
      return this.currentScreen === Screens.VerificationSuccessful
    },
  },

  created () {
    if (this.$route.query.vtoken) this.verifyToken()
    else this.showRequestFormScreen()
  },

  methods: {
    clearUrlQueryParams () {
      if (!Object.keys(this.$route.query).length) return
      this.$router.push({ path: this.$route.path, query: {} })
    },

    closeScreen () {
      this.clearUrlQueryParams()
      this.$emit('on-close')
    },

    requestAccountVerification () {
      this.showLoadingScreen()
      this.createUserAccountVerification({
        email: this.email,
        login: this.login,
      }).
        then(_ => this.showRequestSuccessfulScreen()).
        catch(error => {
          this.errors = error.response?.data?.errors || []
          this.showRequestFailedScreen()
        })
    },

    showLoadingScreen () {
      this.currentScreen = Screens.Loading
    },

    showRequestFailedScreen () {
      this.currentScreen = Screens.RequestFailed
    },

    showRequestFormScreen () {
      this.clearUrlQueryParams()
      this.currentScreen = Screens.RequestForm
    },

    showRequestSuccessfulScreen () {
      this.currentScreen = Screens.RequestSuccessful
    },

    showVerificationFailedScreen () {
      this.currentScreen = Screens.VerificationFailed
    },

    showVerificationSuccessfulScreen () {
      this.currentScreen = Screens.VerificationSuccessful
    },

    verifyToken () {
      this.showLoadingScreen()
      this.verifyUserAccount(this.$route.query.vtoken).
        then(_ => this.showVerificationSuccessfulScreen()).
        catch(error => {
          this.errors = error.response?.data?.errors || []
          this.showVerificationFailedScreen()
        })
    },

    ...mapActions(USER_NAMESPACE, [
      'createUserAccountVerification',
      'verifyUserAccount',
    ]),
  },
}
</script>
